<article class="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
  <header
    class="flex flex-col gap-y-3 border-b border-border p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-x-5"
  >
    <div class="flex flex-col gap-y-0.5">
      <h2 class="m-0 text-lg font-semibold text-primary">Campaign structure</h2>
      <p class="m-0 text-sm text-secondary">
        Open a campaign to see its ad groups, then its Spark ads.
      </p>
    </div>

    @if (hasExpandableRows()) {
      <div class="flex shrink-0 flex-row gap-x-2">
        <button type="button" (click)="expandAll()" class="secondary-button">Expand all</button>
        <button type="button" (click)="collapseAll()" class="secondary-button">Collapse</button>
      </div>
    }
  </header>

  @if (loadState() === 'loading') {
    <div aria-busy="true" aria-label="Loading campaign structure" class="flex flex-col">
      @for (row of [1, 2, 3, 4]; track row) {
        <div class="flex flex-row items-center gap-x-4 border-b border-border/60 px-5 py-4">
          <div class="h-4 flex-1 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
          <div class="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
          <div class="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
        </div>
      }
    </div>
  } @else if (loadState() === 'error') {
    <p role="alert" class="m-0 p-8 text-center text-sm text-error">
      We could not load the campaign structure.
    </p>
  } @else if (isEmpty()) {
    <p class="m-0 p-8 text-center text-sm text-secondary">
      No campaign has been built for this brief yet.
    </p>
  } @else {
    <!-- Horizontal scroll container so the metric columns never squash on narrow screens -->
    <div class="w-full overflow-x-auto">
      <div class="min-w-[52rem]">
        <!-- Header row: identical grid template to every data row -->
        <div
          class="grid items-center gap-x-3 border-b border-border bg-muted/60 px-5 py-2.5 text-xs font-bold tracking-[0.06em] text-secondary uppercase"
          style="grid-template-columns: minmax(0, 1fr) 6rem 5.5rem 5rem 4rem 5rem 4rem"
        >
          <p class="m-0">Name</p>
          <p class="m-0">Status</p>
          <p class="m-0 text-right">Spend</p>
          <p class="m-0 text-right">Impr.</p>
          <p class="m-0 text-right">CTR</p>
          <p class="m-0 text-right">Conv.</p>
          <p class="m-0 text-right">ROAS</p>
        </div>

        @for (row of rows(); track row.node.id) {
          <div
            class="grid items-center gap-x-3 border-b border-border/60 px-5 py-3.5 transition-colors last:border-b-0"
            [class]="rowClassFor(row)"
            style="grid-template-columns: minmax(0, 1fr) 6rem 5.5rem 5rem 4rem 5rem 4rem"
          >
            <div class="flex min-w-0 flex-row items-center gap-x-2">
              <div aria-hidden="true" class="shrink-0" [style.width]="indentFor(row)"></div>

              @if (row.hasChildren) {
                <button
                  type="button"
                  (click)="toggle(row)"
                  [attr.aria-expanded]="row.isOpen"
                  class="shrink-0 cursor-pointer rounded p-0.5 text-brand-studio-dark transition-transform duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  [class.rotate-90]="row.isOpen"
                >
                  <span class="sr-only">
                    {{ row.isOpen ? 'Collapse' : 'Expand' }} {{ row.node.name }}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                    class="size-3.5"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              } @else {
                <div aria-hidden="true" class="size-4 shrink-0"></div>
              }

              <p
                class="m-0 shrink-0 rounded px-1.5 py-0.5 text-[0.625rem] font-bold tracking-[0.06em]"
                [class]="tagClassFor(row.node)"
              >
                <span class="sr-only">{{ levelNameFor(row.node) }}:</span>
                {{ tagFor(row.node) }}
              </p>

              <p class="m-0 truncate" [class]="nameClassFor(row)">{{ row.node.name }}</p>
            </div>

            <div>
              <p class="m-0" [class]="statusPillClassFor(row.node)">
                {{ statusLabelFor(row.node) }}
              </p>
            </div>

            <p class="m-0 text-right text-sm font-bold tabular-nums text-primary">
              {{ spendFor(row.node) }}
            </p>
            <p class="m-0 text-right text-sm font-semibold tabular-nums text-secondary">
              {{ impressionsFor(row.node) }}
            </p>
            <p class="m-0 text-right text-sm font-semibold tabular-nums text-secondary">
              {{ ctrFor(row.node) }}
            </p>
            <p class="m-0 text-right text-sm font-semibold tabular-nums text-secondary">
              {{ conversionsFor(row.node) }}
            </p>
            <p class="m-0 text-right text-sm font-bold tabular-nums text-success">
              {{ roasFor(row.node) }}
            </p>
          </div>
        }
      </div>
    </div>
  }
</article>
